import { createSlice } from "@reduxjs/toolkit";
import {
  getAllAdminsWithPermissionsThunk,
  updateAdminPermissionsThunk,
  applyPresetRoleThunk,
  getPermissionTemplatesThunk,
  checkPermissionThunk,
  getPendingApprovalsThunk,
  approveRequestThunk,
  rejectRequestThunk,
  getApprovalHistoryThunk,
  createApprovalRequestThunk,
  clearPermissionError,
  setApprovalFilter,
  setSelectedAdmin,
} from "../thunks/permissionThunks";

const initialState = {
  // Permission data
  adminsWithPermissions: [],
  selectedAdminForPermissions: null,
  permissionTemplates: {},
  currentPermissionCheck: null,

  // Approval data
  pendingApprovals: [],
  approvalHistory: [],
  approvalFilters: {
    status: "PENDING",
    type: "ALL",
    dateRange: null,
  },

  // Loading states
  loadingPermissions: false,
  loadingApprovals: false,
  loadingTemplates: false,
  savingPermissions: false,

  // Error handling
  error: null,

  // Constants
  permissionModules: [
    { key: "rates", label: "Rates Management" },
    { key: "pricingRules", label: "Pricing Rules" },
    { key: "offers", label: "Offers & Discounts" },
    { key: "products", label: "Products" },
    { key: "reports", label: "Reports" },
    { key: "customers", label: "Customers" },
    { key: "branches", label: "Branches" },
    { key: "admins", label: "Admin Management" },
  ],

  permissionActions: [
    { key: "view", label: "View" },
    { key: "create", label: "Create" },
    { key: "update", label: "Update" },
    { key: "delete", label: "Delete" },
    { key: "approve", label: "Approve" },
    { key: "schedule", label: "Schedule" },
    { key: "export", label: "Export" },
  ],

  approvalTypes: [
    { key: "RATE_UPDATE", label: "Rate Update" },
    { key: "PRICING_RULE_CREATE", label: "Pricing Rule" },
    { key: "OFFER_CREATE", label: "Offer Creation" },
    { key: "HIGH_DISCOUNT", label: "High Discount" },
    { key: "BRANCH_CREATE", label: "Branch Creation" },
  ],

  rolePresets: [
    { key: "viewer", label: "Viewer" },
    { key: "staff", label: "Staff" },
    { key: "manager", label: "Manager" },
    { key: "admin", label: "Admin" },
    { key: "custom", label: "Custom" },
  ],
};

const permissionSlice = createSlice({
  name: "permission",
  initialState,
  reducers: {
    updateTempPermissions: (state, action) => {
      if (state.selectedAdminForPermissions) {
        state.selectedAdminForPermissions.tempPermissions = action.payload;
      }
    },
    resetPermissionState: (state) => {
      state.selectedAdminForPermissions = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Clear error
      .addCase(clearPermissionError, (state) => {
        state.error = null;
      })
      // Set approval filter
      .addCase(setApprovalFilter, (state, action) => {
        state.approvalFilters = { ...state.approvalFilters, ...action.payload };
      })
      // Set selected admin
      .addCase(setSelectedAdmin, (state, action) => {
        state.selectedAdminForPermissions = action.payload;
      })

      // Get All Admins with Permissions
      .addCase(getAllAdminsWithPermissionsThunk.pending, (state) => {
        state.loadingPermissions = true;
        state.error = null;
      })
      .addCase(getAllAdminsWithPermissionsThunk.fulfilled, (state, action) => {
        state.loadingPermissions = false;
        state.adminsWithPermissions = action.payload.data;
      })
      .addCase(getAllAdminsWithPermissionsThunk.rejected, (state, action) => {
        state.loadingPermissions = false;
        state.error = action.payload;
      })

      // Update Admin Permissions
      .addCase(updateAdminPermissionsThunk.pending, (state) => {
        state.savingPermissions = true;
        state.error = null;
      })
      .addCase(updateAdminPermissionsThunk.fulfilled, (state, action) => {
        state.savingPermissions = false;
        const updatedAdmin = action.payload.admin;
        state.adminsWithPermissions = state.adminsWithPermissions.map((admin) =>
          admin._id === updatedAdmin._id ? updatedAdmin : admin
        );
        state.selectedAdminForPermissions = null;
      })
      .addCase(updateAdminPermissionsThunk.rejected, (state, action) => {
        state.savingPermissions = false;
        state.error = action.payload;
      })

      // Apply Preset Role
      .addCase(applyPresetRoleThunk.pending, (state) => {
        state.savingPermissions = true;
        state.error = null;
      })
      .addCase(applyPresetRoleThunk.fulfilled, (state, action) => {
        state.savingPermissions = false;
        const updatedAdmin = action.payload.admin;
        state.adminsWithPermissions = state.adminsWithPermissions.map((admin) =>
          admin._id === updatedAdmin._id ? updatedAdmin : admin
        );
      })
      .addCase(applyPresetRoleThunk.rejected, (state, action) => {
        state.savingPermissions = false;
        state.error = action.payload;
      })

      // Get Permission Templates
      .addCase(getPermissionTemplatesThunk.pending, (state) => {
        state.loadingTemplates = true;
        state.error = null;
      })
      .addCase(getPermissionTemplatesThunk.fulfilled, (state, action) => {
        state.loadingTemplates = false;
        state.permissionTemplates = action.payload.data;
      })
      .addCase(getPermissionTemplatesThunk.rejected, (state, action) => {
        state.loadingTemplates = false;
        state.error = action.payload;
      })

      // Check Permission
      .addCase(checkPermissionThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(checkPermissionThunk.fulfilled, (state, action) => {
        state.currentPermissionCheck = action.payload;
      })
      .addCase(checkPermissionThunk.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Get Pending Approvals
      .addCase(getPendingApprovalsThunk.pending, (state) => {
        state.loadingApprovals = true;
        state.error = null;
      })
      .addCase(getPendingApprovalsThunk.fulfilled, (state, action) => {
        state.loadingApprovals = false;
        state.pendingApprovals = action.payload.data;
      })
      .addCase(getPendingApprovalsThunk.rejected, (state, action) => {
        state.loadingApprovals = false;
        state.error = action.payload;
      })

      // Approve Request
      .addCase(approveRequestThunk.pending, (state) => {
        state.savingPermissions = true;
        state.error = null;
      })
      .addCase(approveRequestThunk.fulfilled, (state, action) => {
        state.savingPermissions = false;
        state.pendingApprovals = state.pendingApprovals.filter(
          (approval) => approval._id !== action.meta.arg.approvalId
        );
      })
      .addCase(approveRequestThunk.rejected, (state, action) => {
        state.savingPermissions = false;
        state.error = action.payload;
      })

      // Reject Request
      .addCase(rejectRequestThunk.pending, (state) => {
        state.savingPermissions = true;
        state.error = null;
      })
      .addCase(rejectRequestThunk.fulfilled, (state, action) => {
        state.savingPermissions = false;
        state.pendingApprovals = state.pendingApprovals.filter(
          (approval) => approval._id !== action.meta.arg.approvalId
        );
      })
      .addCase(rejectRequestThunk.rejected, (state, action) => {
        state.savingPermissions = false;
        state.error = action.payload;
      })

      // Get Approval History
      .addCase(getApprovalHistoryThunk.pending, (state) => {
        state.loadingApprovals = true;
        state.error = null;
      })
      .addCase(getApprovalHistoryThunk.fulfilled, (state, action) => {
        state.loadingApprovals = false;
        state.approvalHistory = action.payload.data;
      })
      .addCase(getApprovalHistoryThunk.rejected, (state, action) => {
        state.loadingApprovals = false;
        state.error = action.payload;
      })

      // Create Approval Request
      .addCase(createApprovalRequestThunk.pending, (state) => {
        state.savingPermissions = true;
        state.error = null;
      })
      .addCase(createApprovalRequestThunk.fulfilled, (state, action) => {
        state.savingPermissions = false;
        state.pendingApprovals.push(action.payload.data);
      })
      .addCase(createApprovalRequestThunk.rejected, (state, action) => {
        state.savingPermissions = false;
        state.error = action.payload;
      });
  },
});

export const { updateTempPermissions, resetPermissionState } =
  permissionSlice.actions;
export default permissionSlice.reducer;
